import type { ExecutionMode } from '@hangar/shared';
import { cn } from '@/lib/utils';
import { EXECUTION_MODE_LABEL } from '@/components/badges';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { isModeUnavailable, visibleModes } from './profileFormModel';
import type { ProfileFormApi } from './useProfileForm';

/** One execution mode checkbox; the Hangar runner is locked (with a tooltip) while that feature is off. */
function ModeOption({ mode, form }: { mode: ExecutionMode; form: ProfileFormApi }) {
  const on = form.values.modes.includes(mode);
  const unavailable = isModeUnavailable(mode, form.runnerOff, on);
  return (
    <label
      title={unavailable ? 'This Hangar has no runner yet' : undefined}
      className={cn('flex h-9 items-center gap-2 rounded-lg border px-3 text-[13px] max-sm:h-11', unavailable ? 'cursor-not-allowed opacity-60' : 'cursor-pointer', on ? 'border-primary/40 bg-primary/10 text-foreground' : 'text-muted-foreground')}
    >
      <Checkbox data-testid={`profile-mode-${mode}`} checked={on} disabled={unavailable} onCheckedChange={(v) => form.setMode(mode, v === true)} />
      {EXECUTION_MODE_LABEL[mode]}
    </label>
  );
}

/** What the modes of the picked build service mean, unless there is an error to show instead (no provider named, SPEC decision 23). */
function ModesHelp({ bitrise }: { bitrise: boolean }) {
  return (
    <p className="text-xs text-subtle">
      {bitrise ? (
        <>{EXECUTION_MODE_LABEL.provider_api}: Run build starts the build on Hangar&apos;s build service.</>
      ) : (
        <>{EXECUTION_MODE_LABEL.hangar_runner}: Run build takes the commit from the app&apos;s repository and sends it to Hangar&apos;s build service.</>
      )}
    </p>
  );
}

export function ProfileModes({ form }: { form: ProfileFormApi }) {
  const error = form.errorFor('executionModes');
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[13px]">
        How builds start
      </Label>
      <div className="flex flex-wrap gap-2">
        {/* The CI client choice is hidden for the MVP (docs/mvp-plan.md P17 brings it back): visibleModes. */}
        {visibleModes(form.allowedModes, form.values.modes).map((m) => (
          <ModeOption key={m} mode={m} form={form} />
        ))}
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : <ModesHelp bitrise={form.bitriseProfile} />}
    </div>
  );
}
